import { useEffect } from 'react';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import * as Yup from 'yup';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Typography,
  withWidth,
} from '@material-ui/core';

import { Divider } from 'components';
import { fromApiPercent, toApiPercent } from 'utils/percent';
import { pickValue, setPickedKeyValue } from 'utils/objects';

import {
  CardSelector,
  DateControl,
  DocumentControl,
  DocumentInlineControl,
  FileControl,
  PhoneNumberControl,
  NumberControl,
  PillSelectControl,
  SelectControl,
  SliderControl,
  SwitchControl,
  SplittedTextControl,
  TextControl,
  NoteControl,
  ButtonControl,
} from './controls';
import FormErrorNotification from './FormErrorNotification';

function FormValuesObserver({ onValuesChange, values }) {
  useEffect(() => {
    if (onValuesChange) {
      onValuesChange(values);
    }
  }, [onValuesChange, values]);

  return null;
}

FormValuesObserver.defaultProps = {
  onValuesChange: undefined,
};

FormValuesObserver.propTypes = {
  onValuesChange: PropTypes.func,
  values: PropTypes.object,
};

const CONTROL = {
  CAPTCHA: 'captcha',
  CARDSELECTOR: 'cardselector',
  CHECKBOX: 'checkbox',
  CODE: 'code',
  DATE: 'date',
  DOCUMENT: 'document',
  NOTE: 'note',
};

const CONTROLS = {
  [CONTROL.CARDSELECTOR]: CardSelector,
  [CONTROL.CHECKBOX]: SwitchControl,
  [CONTROL.DATE]: DateControl,
  divider: '', // FIXME: Delete type
  [CONTROL.DOCUMENT]: DocumentControl,
  document_inline: DocumentInlineControl,
  email: TextControl,
  file: FileControl,
  hidden: TextControl,
  phone_number: PhoneNumberControl,
  number: NumberControl,
  password: TextControl,
  pill_select: PillSelectControl,
  select: SelectControl,
  slider: SliderControl,
  switch: SwitchControl,
  splitted: SplittedTextControl,
  text: TextControl,
  textarea: TextControl,
  note: NoteControl,
};

const cloneValue = (value) => {
  if (value === null || value === undefined || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(cloneValue);
  }

  return Object.keys(value).reduce((acc, key) => {
    acc[key] = cloneValue(value[key]);
    return acc;
  }, {});
};

const flattenFields = (fields = []) => fields.flatMap((field) => {
  if (Array.isArray(field)) {
    return flattenFields(field);
  }
  return field ? [field] : [];
});

const getPercentageFieldNames = (fields) => flattenFields(fields)
  .filter((field) => field?.settings?.format === 'percentage' && field?.name)
  .map((field) => field.name);

const applyPercentageTransform = (values, fieldNames, transform) => {
  if (!values || typeof values !== 'object' || fieldNames.length === 0) {
    return values;
  }

  const nextValues = cloneValue(values);

  fieldNames.forEach((fieldName) => {
    const currentValue = pickValue(nextValues, fieldName);
    const transformedValue = transform(currentValue);

    if (transformedValue !== currentValue) {
      setPickedKeyValue(nextValues, fieldName, transformedValue);
    }
  });

  return nextValues;
};

function getControl(opts) {
  const { settings } = opts;
  let { type } = opts;

  if (settings?.format === 'phone_number' || settings?.format === 'mx_phone') {
    type = 'phone_number';
  }

  return CONTROLS[type] || TextControl;
}

function DynamicForm(props) {
  const {
    actions,
    debug,
    fields,
    handleSubmitData,
    id,
    initialValues,
    onValidationError,
    spacing,
    validationSchema,
    width,
  } = props;

  const percentageFieldNames = Array.isArray(fields)
    ? getPercentageFieldNames(fields)
    : [];
  const resolvedInitialValues = applyPercentageTransform(
    initialValues,
    percentageFieldNames,
    fromApiPercent,
  );

  async function onSubmit(values, formikHelpers) {
    const {
      // resetForm,
      setErrors,
      setStatus,
      setSubmitting,
    } = formikHelpers;

    try {
      await setSubmitting(true);
      const castValues = validationSchema.cast(values);
      const normalizedValues = applyPercentageTransform(
        castValues,
        percentageFieldNames,
        toApiPercent,
      );
      await handleSubmitData(normalizedValues);

      setStatus({
        success: true,
      });
    } catch (error) {
      setStatus({
        success: false,
      });
      setErrors({
        submit: error.message,
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (!Array.isArray(fields)) {
    const Control = getControl(fields);

    return <Control
      {...fields}
      size={props.size}
      variant={props.variant}
    />;
  }

  return (
    <Formik
      initialValues={resolvedInitialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formikProps) => {
        const {
          errors,
          handleBlur,
          handleChange,
          handleReset,
          handleSubmit,
          // isSubmitting,
          // isValid,
          setFieldTouched,
          setFieldValue,
          touched,
          values,
        } = formikProps;

        const formApi = {
          setFieldValue,
          values,
        };

        const resolveEvents = (events = {}) => {
          if (!events?.onChange) {
            return events;
          }

          return {
            ...events,
            onChange: (target) => events.onChange(target, formApi),
          };
        };

        const onSubmitForm = function (e) {
          handleSubmit(e);

          fields.forEach((f) => {
            if (f.name) {
              setFieldTouched(f.name, true);
            }
          });
        };

        return (
          <Form onReset={handleReset} onSubmit={onSubmitForm} id={id}>
            <FormValuesObserver
              onValuesChange={props.onValuesChange}
              values={values}
            />
            <FormErrorNotification
              debug={debug}
              onValidationError={onValidationError}
            />

            <Grid container spacing={spacing}>
              {fields
                .map((item, index) => {
                  const Control = getControl(item);
                  const { display: { size = props.size } = {} } = item;

                  let {
                    display: {
                      breakpoints = {
                        md: 12,
                      },
                    } = {},
                  } = item;

                  switch (item.type) {
                    case CONTROL.CAPTCHA:
                    case CONTROL.CARDSELECTOR:
                    case CONTROL.DOCUMENT:
                      breakpoints = {
                        lg: 4,
                        xl: 4,
                        md: 6,
                        sm: 12,
                        xs: 12,
                      };
                      break;

                    default:
                  }

                  // FIXME: Eliminar, pasar a usar type: 'component'
                  if (item.type === 'actions') {
                    return (<Grid key={index} item {...breakpoints}>
                      {item?.settings?.options.map((option, index) => (
                        <IconButton
                          children={option.icon}
                          disabled={option.disabled}
                          key={index}
                          size="small"
                          onClick={option.fn}
                        />
                      ))}
                    </Grid>);
                  }

                  // FIXME: Eliminar, pasar a usar type: 'component'
                  if (item.type === 'description') {
                    return (<Grid key={index} item {...breakpoints} style={item.styleCont}>
                      <Typography
                        align="justify"
                        children={item.label}
                      />
                    </Grid>);
                  }

                  if (item.type === 'component') {
                    return (
                      <Grid key={index} item {...breakpoints} style={item.styleCont}>
                        {item.component}
                      </Grid>
                    );
                  }

                  // FIXME: Eliminar, pasar a usar type: 'component'
                  if (item.type === 'link') {
                    return (<Grid key={index} item {...breakpoints}>
                      <Link
                        children={item.label}
                        color={item.color}
                        component={RouterLink}
                        to={item.to}
                        variant="body2"
                      />
                    </Grid>);
                  }

                  // FIXME: Eliminar, pasar a usar type: 'component'
                  if (item.type === 'btn_link_submit') {
                    return (<Grid key={index} item {...breakpoints} style={item.styleCont}>
                      <Button
                        style={item.style}
                        color={item.color}
                        disabled={item.processing}
                        fullWidth
                        size="large"
                        type={'submit'}
                        variant="contained"
                        onClick={() => item.fn()}
                      >
                        {item.processing && (<CircularProgress
                          size={18}
                          style={{
                            marginRight: 5,
                          }}
                        />)}
                        {item.label}
                      </Button>
                    </Grid>);
                  }

                  // FIXME: Eliminar, pasar a usar type: 'component'
                  if (item.type === 'space') {
                    return (<Grid key={index} item {...breakpoints}>
                      <div style={{
                        height: `${width === 'md' || width === 'lg' ? item.height : 0}px`,
                      }}>&nbsp;</div>
                    </Grid>);
                  }

                  // FIXME: Eliminar, pasar a usar type: 'component'
                  if (item.type === 'offset') {
                    return breakpoints[width] !== 12 ? (<Grid
                      item
                      key={index}
                      {...breakpoints}
                    />) : null;
                  }

                  // FIXME: Eliminar, pasar a usar type: 'component'
                  if (item.type === 'divider') {
                    return (<Grid
                      children={<Divider
                        align={item?.display?.align}
                        children={item.label}
                        variant={item?.display?.variant || props.variant}
                      />}
                      key={index}
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xl={12}
                      xs={12}
                    />);
                  }

                  if (item.type === 'hidden') {
                    return (<></>);
                  } else {
                    const resolvedEvents = resolveEvents(item.events);
                    const gridControls = [];

                    if (item?.display?.offset?.before) {
                      gridControls.push(
                        <Grid
                          item
                          {...item?.display?.offset?.before}
                        />);
                    }

                    gridControls.push(<Grid
                      key={`${item.name || ''}_grid_${index}`}
                      item
                      {...breakpoints}
                      data-field-name={item.name}
                    >
                      <Control
                        {...item}
                        events={resolvedEvents}
                        key={`${item.name || ''}_${index}`}
                        error={pickValue(touched, item.name) && pickValue(errors, item.name)}
                        helperText={pickValue(touched, item.name) ? pickValue(errors, item.name) : null}
                        size={size}
                        value={item.clearValue ? '' : pickValue(values, item.name)}
                        onBlur={handleBlur}
                        onChange={async (event) => {
                          handleChange(event);
                        }}
                        variant={item?.display?.variant || props.variant}
                      />
                    </Grid>);

                    if (item?.display?.offset?.after) {
                      gridControls.push(
                        <Grid
                          item
                          {...item?.display?.offset?.after}
                        />);
                    }

                    return gridControls;
                  }
                })}
            </Grid>

            {actions && actions
              .filter((_) => !_.hidden)
              .map((action, index) => (
                <Box key={index} mt={2}>
                  {/* FIXME: Eliminar, pasar a usar type: 'component' */}
                  {action.typeButton === 'login' ? (
                    <ButtonControl
                      label={action.label}
                      color={action.color}
                      disabled={action.processing}
                      fullWidth
                      size="large"
                      type='submit'
                      variant="contained"
                    />
                  ) : (
                    <Button
                      color={action.color}
                      disabled={action.processing}
                      fullWidth
                      size="large"
                      type={action.type}
                      variant="contained"
                    >
                      {action.processing && (<CircularProgress
                        size={18}
                        style={{
                          marginRight: 5,
                        }}
                      />)}
                      {action.label}
                    </Button>
                  )}
                </Box>
              ))}

          </Form>
        );
      }}
    </Formik>
  );
}

const propFields = PropTypes.shape({
  display: PropTypes.shape({
    breakpoints: PropTypes.shape({
      lg: PropTypes.number,
      md: PropTypes.number,
      sm: PropTypes.number,
      xl: PropTypes.number,
      xs: PropTypes.number,
    }),
    inputStyle: PropTypes.object,
    labelStyle: PropTypes.object,
    size: PropTypes.oneOf([
      'small',
      'medium',
    ]),
    variant: PropTypes.oneOf([
      'outlined',
      'standard',
    ]),
  }),
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  name: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.oneOf(Object.keys(CONTROLS)),
  settings: PropTypes.shape({
    emptyElement: PropTypes.string,
    format: PropTypes.oneOf([
      'clabe',
      'credit_card',
      'credit_card_expire_time',
      'currency',
      'mx_phone',
      'percentage',
      'phone_number',
    ]),
    max: PropTypes.number,
    min: PropTypes.number,
    multiline: PropTypes.bool,
    options: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),
        name: PropTypes.string,
      })),
      PropTypes.func,
    ]),
    rows: PropTypes.number,
    rowsMax: PropTypes.number,
    step: PropTypes.number,
    clearValue: PropTypes.bool,
  }),
});

DynamicForm.defaultProps = {
  debug: false,
  fields: [],
  handleSubmitData: () => { },
  id: `dynamic_form_${Math.floor(Math.random() * 10000)}`,
  initialValues: {},
  onValidationError: () => { },
  onValuesChange: undefined,
  spacing: 2,
  size: 'small',
  validationSchema: Yup.object(),
  variant: 'outlined',
};

DynamicForm.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.oneOf(['primary', 'secondary']),
    hidden: PropTypes.bool,
    label: PropTypes.string,
    type: PropTypes.oneOf(['submit']),
    processing: PropTypes.bool,
  })),
  debug: PropTypes.bool,
  fields: PropTypes.oneOfType([
    PropTypes.arrayOf(propFields),
    propFields,
  ]),
  handleSubmitData: PropTypes.func,
  id: PropTypes.string,
  initialValues: PropTypes.object,
  onValidationError: PropTypes.func,
  onValuesChange: PropTypes.func,
  spacing: PropTypes.number,
  size: PropTypes.oneOf([
    'small',
    'medium',
  ]),
  validationSchema: PropTypes.object,
  variant: PropTypes.oneOf([
    'outlined',
    'standard',
  ]),
  width: PropTypes.string,
};

export default withWidth()(DynamicForm);
